// ============================================================
// SocialHub — friend list / blocklist / online search UI
// ============================================================
//
// Two layout modes:
//   'modal'      — in-game overlay, dims background, close on Esc / X.
//   'fullscreen' — main-menu entry point, consumes the whole canvas.
//
// Left side: tabs (Friends / Pending / Blocked / Online / Guild placeholder).
// Top: search box + Add Friend button.
// Right: scrolling entry list with per-row actions.
//
// This is a sample implementation — minimal styling, functional first. Plenty
// of room to polish if the feature sticks.

import Phaser from 'phaser';
import {
  SOCIAL_PAGE_SIZE,
  type GameMode,
  type SocialActivity,
  type SocialListMode,
  type SocialRelation,
  type SocialUserSummary,
} from '@roe/shared';
import { SocialService } from '../services/SocialService.js';
import { MultiplayerService } from '../services/MultiplayerService.js';
import { ChatContextMenu } from './ChatContextMenu.js';

/** Activity-pill visual mapping. Single source of truth so cell rendering
 *  and tests can reference the exact label/colors in one place. */
const ACTIVITY_VIZ: Record<SocialActivity, { label: string; dot: number; text: string }> = {
  'mp-menu': { label: 'In lobby', dot: 0x7acc60, text: '#7acc60' },
  'menu':    { label: 'Online',   dot: 0xc8973a, text: '#c8973a' },
  'offline': { label: 'Offline',  dot: 0x5a3600, text: '#5a4830' },
};

type Mode = 'modal' | 'fullscreen';
type TabKey = SocialListMode | 'guild';

export interface SocialHubOpts {
  mode: Mode;
  sceneWidth: number;
  sceneHeight: number;
  onClose?: () => void;
  /** Fired when the user requests a whisper with a row's target — either via
   *  right-click or the explicit Whisper button. Scene is responsible for
   *  opening the whisper tab in its ChatOverlay. In modal mode the hub also
   *  closes itself to get out of the way. */
  onWhisperRequested?: (userId: string, name: string) => void;
  /** Present when the hub is opened from a multiplayer lobby scene. Unlocks
   *  the "invite" action button on online-friend rows. Missing/undefined
   *  when opened from the main menu — no invite button in that case. */
  mpMenuContext?: { mode: GameMode };
}

export class SocialHub {
  private scene: Phaser.Scene;
  private opts: SocialHubOpts;
  private root: Phaser.GameObjects.Container;
  private dim?: Phaser.GameObjects.Rectangle;

  private activeTab: TabKey = 'friends';
  private page = 0;
  private rows: Phaser.GameObjects.GameObject[] = [];
  private tabsContainer!: Phaser.GameObjects.Container;

  private searchBg!: Phaser.GameObjects.Rectangle;
  private searchText!: Phaser.GameObjects.Text;
  private searchDraft = '';
  private searchFocused = false;
  private addBtn!: Phaser.GameObjects.Text;
  private pageLabel!: Phaser.GameObjects.Text;

  private unsubUpdate?: () => void;
  private keyHandler?: (ev: KeyboardEvent) => void;

  private panelX: number;
  private panelY: number;
  private panelW: number;
  private panelH: number;

  constructor(scene: Phaser.Scene, opts: SocialHubOpts) {
    this.scene = scene;
    this.opts = opts;
    const DEPTH = 200;

    if (opts.mode === 'modal') {
      this.panelW = Math.min(720, opts.sceneWidth - 40);
      this.panelH = Math.min(480, opts.sceneHeight - 60);
    } else {
      this.panelW = Math.min(800, opts.sceneWidth - 80);
      this.panelH = Math.min(540, opts.sceneHeight - 80);
    }
    this.panelX = Math.round((opts.sceneWidth - this.panelW) / 2);
    this.panelY = Math.round((opts.sceneHeight - this.panelH) / 2);

    // Both modes cover the scene with an interactive rectangle to absorb clicks
    // behind the hub (`input.topOnly` is true by default in Phaser, so the top-
    // depth interactive object wins the hit-test and underlying game objects
    // don't receive the pointer event). Modal uses a dimmed backdrop; fullscreen
    // uses a fully opaque one so the main menu below is visually replaced too.
    // Both modes use a semi-transparent backdrop so the scene below
    // (MenuScene in fullscreen, BattleScene in modal) stays visible —
    // previously fullscreen was fully opaque which hid the main menu.
    const backdropAlpha = 0.55;
    this.dim = scene.add.rectangle(
      opts.sceneWidth / 2, opts.sceneHeight / 2,
      opts.sceneWidth, opts.sceneHeight,
      0x000000, backdropAlpha,
    ).setDepth(DEPTH - 1).setInteractive();
    this.root = scene.add.container(0, 0).setDepth(DEPTH);

    // Panel bg + border
    const bg = scene.add.rectangle(this.panelX, this.panelY, this.panelW, this.panelH, 0x0a0608, 0.98)
      .setOrigin(0, 0).setStrokeStyle(1, 0x5a3600, 0.95);
    this.root.add(bg);

    // Title
    this.root.add(scene.add.text(this.panelX + 14, this.panelY + 10, '✧  Social Hub', {
      fontSize: '14px', fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#f0c040', fontStyle: 'bold',
    }));

    // Close ✕
    const closeBtn = scene.add.text(this.panelX + this.panelW - 20, this.panelY + 8, '✕', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#8a6420', fontStyle: 'bold',
    }).setOrigin(0.5, 0).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this.close());
    closeBtn.on('pointerover', () => closeBtn.setColor('#f0c040'));
    closeBtn.on('pointerout',  () => closeBtn.setColor('#8a6420'));
    this.root.add(closeBtn);

    // ── Search box + Add Friend ──
    const searchX = this.panelX + 14;
    const searchY = this.panelY + 36;
    const searchW = this.panelW - 160;
    this.searchBg = scene.add.rectangle(searchX, searchY, searchW, 22, 0x0d0a08, 1)
      .setOrigin(0, 0).setStrokeStyle(1, 0x6a3a00).setInteractive({ useHandCursor: true });
    this.searchBg.on('pointerdown', () => this.focusSearch());
    this.root.add(this.searchBg);
    this.searchText = scene.add.text(searchX + 8, searchY + 11, 'Search by username…', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
    }).setOrigin(0, 0.5);
    this.root.add(this.searchText);

    this.addBtn = scene.add.text(this.panelX + this.panelW - 130, searchY + 11, '+ Add Friend', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
      backgroundColor: '#1a1008', padding: { x: 8, y: 4 },
    }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    this.addBtn.on('pointerdown', () => this.tryAddFromSearch());
    this.addBtn.on('pointerover', () => this.addBtn.setColor('#f0c040'));
    this.addBtn.on('pointerout',  () => this.addBtn.setColor('#c8973a'));
    this.root.add(this.addBtn);

    // ── Left tab column ──
    const tabX = this.panelX + 14;
    const tabY = this.panelY + 74;
    this.tabsContainer = scene.add.container(0, 0);
    this.root.add(this.tabsContainer);
    const tabs: Array<{ key: TabKey; label: string }> = [
      { key: 'friends', label: 'Friends'     },
      { key: 'pending', label: 'Requests'    },
      { key: 'blocked', label: 'Blocked'     },
      { key: 'online',  label: 'All Online'  },
      { key: 'guild',   label: 'Guild'       },
    ];
    tabs.forEach((t, i) => {
      const btn = scene.add.rectangle(tabX, tabY + i * 30, 128, 26, 0x1a1008, 0.95)
        .setOrigin(0, 0).setStrokeStyle(1, 0x5a3600).setInteractive({ useHandCursor: true })
        .setName(`tab_${t.key}`);
      const txt = scene.add.text(tabX + 12, tabY + i * 30 + 13, t.label, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#6b4020',
      }).setOrigin(0, 0.5).setName(`tabTxt_${t.key}`);
      btn.on('pointerdown', () => this.setTab(t.key));
      btn.on('pointerover', () => { if (this.activeTab !== t.key) txt.setColor('#c8973a'); });
      btn.on('pointerout',  () => { if (this.activeTab !== t.key) txt.setColor('#6b4020'); });
      this.tabsContainer.add(btn);
      this.tabsContainer.add(txt);
    });

    // Pagination label (used only for `online` tab).
    this.pageLabel = scene.add.text(this.panelX + this.panelW - 14, this.panelY + this.panelH - 14, '', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#8a6420',
    }).setOrigin(1, 1);
    this.root.add(this.pageLabel);

    // Cleanup hooks
    this.keyHandler = (ev: KeyboardEvent) => this.handleKey(ev);
    window.addEventListener('keydown', this.keyHandler, true);

    const ss = SocialService.getInstance();
    ss.ensureWired();
    this.unsubUpdate = ss.onUpdate((u) => {
      if ((u.kind === 'friends-changed' && (this.activeTab === 'friends' || this.activeTab === 'online'))
       || (u.kind === 'pending-changed' && this.activeTab === 'pending')
       || (u.kind === 'blocked-changed' && this.activeTab === 'blocked')) {
        this.refreshList();
      }
    });

    scene.events.once('shutdown', () => this.destroy());
    scene.events.once('destroy',  () => this.destroy());

    this.renderTabs();
    this.refreshList();
  }

  // ---------- Search ----------

  private focusSearch(): void {
    this.searchFocused = true;
    this.searchBg.setStrokeStyle(1, 0xc8973a);
    if (!this.searchDraft) this.searchText.setText('|').setColor('#d8a03a');
    else this.searchText.setColor('#d8a03a');
  }
  private blurSearch(): void {
    this.searchFocused = false;
    this.searchBg.setStrokeStyle(1, 0x6a3a00);
    if (!this.searchDraft) this.searchText.setText('Search by username…').setColor('#5a3600');
  }
  private setSearch(v: string): void {
    this.searchDraft = v;
    this.searchText.setText(v.length > 0 ? v + (this.searchFocused ? '|' : '') : (this.searchFocused ? '|' : 'Search by username…'));
    this.searchText.setColor(v.length > 0 ? '#d8a03a' : (this.searchFocused ? '#d8a03a' : '#5a3600'));
  }

  private async tryAddFromSearch(): Promise<void> {
    const q = this.searchDraft.trim();
    if (q.length < 2) return;
    const ss = SocialService.getInstance();
    const res = await ss.search(q);
    if (!res || res.entries.length === 0) {
      this.toast(`No user matches "${q}"`);
      return;
    }
    // First match sends the friend request.
    ss.sendFriendRequest(res.entries[0].id);
    this.toast(`Friend request sent to ${res.entries[0].name}`);
    this.setSearch('');
  }

  /** Ack-driven invite send. The previous pattern fired an optimistic
   *  "Invite sent" toast synchronously, which silently swallowed server-side
   *  rejections without an `inviteId` (e.g. `target-not-in-menu`,
   *  `target-offline`, `rate-limit`). The user saw "✅ sent" while the
   *  recipient never received anything. Now the toast reflects the server's
   *  ack and surfaces send-side errors. */
  private async _sendInviteWithFeedback(targetId: string, targetName: string): Promise<void> {
    const result = await MultiplayerService.getInstance().sendInviteWithFeedback(targetId);
    if (result.ok) {
      this.toast(`Invite sent to ${targetName}`);
    } else {
      this.toast(result.message);
    }
  }

  private toast(text: string): void {
    const t = this.scene.add.text(this.panelX + this.panelW / 2, this.panelY + this.panelH - 24, text, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#f0c040',
      backgroundColor: '#1a1008', padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 1).setDepth(this.root.depth + 10);
    this.scene.tweens.add({ targets: t, alpha: 0, delay: 1800, duration: 320, onComplete: () => t.destroy() });
  }

  // ---------- Tabs ----------

  private setTab(tab: TabKey): void {
    this.activeTab = tab;
    this.page = 0;
    this.renderTabs();
    this.refreshList();
  }

  private renderTabs(): void {
    const keys: TabKey[] = ['friends', 'pending', 'blocked', 'online', 'guild'];
    for (const k of keys) {
      const btn = this.tabsContainer.getByName(`tab_${k}`) as Phaser.GameObjects.Rectangle;
      const txt = this.tabsContainer.getByName(`tabTxt_${k}`) as Phaser.GameObjects.Text;
      const active = k === this.activeTab;
      btn.setFillStyle(active ? 0x2a1810 : 0x1a1008, active ? 0.95 : 0.85);
      btn.setStrokeStyle(1, active ? 0xc8973a : 0x5a3600);
      txt.setColor(active ? '#f0c040' : '#6b4020');
    }
  }

  // ---------- List rendering ----------

  private clearRows(): void {
    for (const r of this.rows) r.destroy();
    this.rows = [];
  }

  private async refreshList(): Promise<void> {
    this.clearRows();
    if (this.activeTab === 'guild') {
      const t = this.scene.add.text(this.panelX + 160, this.panelY + 120, '— No guild yet —', {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
      }).setDepth(205);
      this.rows.push(t);
      this.pageLabel.setText('');
      return;
    }
    const ss = SocialService.getInstance();
    const res = await ss.list(this.activeTab, this.page);
    if (!res) {
      const t = this.scene.add.text(this.panelX + 160, this.panelY + 120, 'Failed to load (are you online?).', {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#ef4444',
      }).setDepth(205);
      this.rows.push(t);
      return;
    }
    // Pagination text — apply to every tab when there are multiple pages.
    if (res.pageSize > 0 && res.total > res.pageSize) {
      const pages = Math.max(1, Math.ceil(res.total / res.pageSize));
      this.pageLabel.setText(`Page ${this.page + 1} / ${pages}   ← → to change`);
    } else {
      this.pageLabel.setText('');
    }
    if (res.entries.length === 0) {
      const t = this.scene.add.text(this.panelX + 160, this.panelY + 120, this.emptyText(), {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
      }).setDepth(205);
      this.rows.push(t);
      return;
    }
    // Defensive client-side cap: server paginates to SOCIAL_PAGE_SIZE but
    // any future schema drift / dev-mode bug / rolling deploy could send a
    // larger payload. Capping here keeps the grid bounded so we never render
    // hundreds of animated portraits below the panel.
    const visible = res.entries.slice(0, SOCIAL_PAGE_SIZE);
    visible.forEach((e, i) => this.renderCell(e, i));
  }

  private emptyText(): string {
    switch (this.activeTab) {
      case 'friends': return 'No friends yet — add someone from the Online tab or search above.';
      case 'pending': return 'No pending requests.';
      case 'blocked': return 'Block list empty.';
      case 'online':  return 'No other players online right now.';
      default:        return '—';
    }
  }

  /** Common "Whisper on this row" action — fire the whisper callback and
   *  close the hub so the chat overlay is visible + focused. Applies to both
   *  modal (in-game) and fullscreen (main-menu) — in both cases the user's
   *  intent is to switch from browsing social to writing a message, so the
   *  hub should get out of the way unconditionally. */
  private triggerWhisper(id: string, name: string): void {
    this.opts.onWhisperRequested?.(id, name);
    this.close();
  }

  /** Rolling-deploy fallback: if the server didn't stamp a relation (older
   *  build), pick the action set based on which tab the row came from. Keeps
   *  the UI functional until the server's upgraded; once it is, `e.relation`
   *  wins and this fallback is never hit. */
  private fallbackRelationForTab(): SocialRelation {
    switch (this.activeTab) {
      case 'friends': return 'friend';
      case 'pending': return 'pending_received';
      case 'blocked': return 'blocked';
      case 'online':  return 'none';
      default:        return 'none';
    }
  }

  private renderCell(e: SocialUserSummary, i: number): void {
    // Grid layout: 4 columns × N rows, capped per page by SOCIAL_PAGE_SIZE.
    // Each cell shows the player's main hero portrait (animated) or a
    // "New player" placeholder, plus pseudo / stage badge / activity dot.
    // Right-click opens the existing ChatContextMenu — there are no inline
    // action buttons, the context menu is the sole action surface.
    const COLS = 4;
    const cellW = 120;
    const cellH = 140;
    const gutterX = 8;
    const gutterY = 8;
    const gridX = this.panelX + 160;
    const gridY = this.panelY + 80;
    const col = i % COLS;
    const row = Math.floor(i / COLS);
    const cx = gridX + col * (cellW + gutterX) + cellW / 2;
    const cy = gridY + row * (cellH + gutterY) + cellH / 2;
    // Cell objects render above the panel bg (DEPTH=200).
    const CELL_DEPTH = 205;

    const bg = this.scene.add.rectangle(cx, cy, cellW, cellH, 0x0d0a08, 0.9)
      .setStrokeStyle(1, 0x3a2800, 0.6).setDepth(CELL_DEPTH)
      .setInteractive({ useHandCursor: false });
    // Right-click opens the ChatContextMenu — the sole action surface for
    // each cell. Reuses the previous row-level pointer-coord conversion so
    // the menu lands at the click position regardless of camera zoom.
    bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!pointer?.rightButtonDown?.()) return;
      const social = SocialService.getInstance();
      const relation: SocialRelation = e.relation ?? this.fallbackRelationForTab();
      let px = pointer.x ?? 0, py = pointer.y ?? 0;
      const nativeEvent = (pointer as unknown as { event?: MouseEvent | PointerEvent }).event;
      const canvas = (this.scene as unknown as { sys?: { game?: { canvas?: HTMLCanvasElement } } }).sys?.game?.canvas;
      if (nativeEvent && canvas) {
        const rect = canvas.getBoundingClientRect();
        px = (nativeEvent.clientX - rect.left) * (this.opts.sceneWidth / rect.width);
        py = (nativeEvent.clientY - rect.top) * (this.opts.sceneHeight / rect.height);
      }
      ChatContextMenu.open(this.scene, {
        x: px, y: py,
        sceneWidth: this.opts.sceneWidth,
        sceneHeight: this.opts.sceneHeight,
        targetUserId: e.id,
        targetName: e.name,
        targetOnline: e.online,
        relation,
        context: this.opts.mpMenuContext ? 'lobby' : 'social-hub',
        lobbyMode: this.opts.mpMenuContext?.mode,
      }, {
        onWhisper: (id, n) => this.triggerWhisper(id, n),
        onInvite: (id) => { void this._sendInviteWithFeedback(id, e.name); },
        onAddFriend: (id) => social.sendFriendRequest(id),
        onRemoveFriend: (id) => social.removeFriend(id),
        onAcceptRequest: (id) => social.acceptFriend(id),
        onCancelRequest: (id) => social.cancelFriendRequest(id),
        onBlock: (id) => social.block(id),
        onUnblock: (id) => social.unblock(id),
      });
    });
    this.rows.push(bg);

    // Pseudo at the top.
    const nameT = this.scene.add.text(cx, cy - cellH / 2 + 12, e.name, {
      fontSize: '11px', fontFamily: 'Georgia, serif',
      color: e.online ? '#c8973a' : '#6b4020', fontStyle: 'bold',
    }).setOrigin(0.5, 0.5).setDepth(CELL_DEPTH + 1);
    this.rows.push(nameT);

    // Body — main hero animated portrait OR "New player" placeholder.
    const body = this.renderCellBody(cx, cy, e, CELL_DEPTH);
    this.rows.push(...body);

    // Stage badge (bottom-left) — only when a solo save exists.
    if (typeof e.highestStage === 'number' && e.highestStage > 0) {
      const stageT = this.scene.add.text(cx - cellW / 2 + 8, cy + cellH / 2 - 12, `Lv ${e.highestStage}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
      }).setOrigin(0, 0.5).setDepth(CELL_DEPTH + 2);
      this.rows.push(stageT);
    }

    // Activity dot + label (bottom-right). Both anchored to the cell's right
    // edge by fixed offsets — using `activityLabel.width` for the dot would
    // cause the dot to drift left as label text grows ("In lobby" vs
    // "Online") and could overlap the stage badge. Map lookup is guarded
    // against malformed/version-skewed values via the `??` fallback so
    // schema drift can't crash the grid render.
    const rawActivity = e.activity ?? (e.online ? 'menu' : 'offline');
    const activity: SocialActivity = ACTIVITY_VIZ[rawActivity as SocialActivity]
      ? (rawActivity as SocialActivity)
      : (e.online ? 'menu' : 'offline');
    const activityViz = ACTIVITY_VIZ[activity];
    const activityRightX = cx + cellW / 2 - 8;
    const activityY = cy + cellH / 2 - 12;
    const dot = this.scene.add.circle(activityRightX - 50, activityY, 3, activityViz.dot, 1)
      .setDepth(CELL_DEPTH + 2);
    this.rows.push(dot);
    const activityLabel = this.scene.add.text(activityRightX, activityY, activityViz.label, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: activityViz.text,
    }).setOrigin(1, 0.5).setDepth(CELL_DEPTH + 2);
    this.rows.push(activityLabel);
  }

  /** Renders the cell body — animated hero sprite when a `mainHero` is
   *  available, otherwise a "New player" placeholder (`?` glyph + caption).
   *  Returns the created Phaser objects so the caller can register them with
   *  `this.rows` for cleanup on tab switch / hub close. */
  private renderCellBody(cx: number, cy: number, e: SocialUserSummary, depth: number): Phaser.GameObjects.GameObject[] {
    const created: Phaser.GameObjects.GameObject[] = [];
    if (e.mainHero) {
      const key = [e.mainHero.heroKey, e.mainHero.heroClass]
        .filter((k): k is string => !!k)
        .find((k) => this.scene.textures.exists(`hero_${k}`));
      if (key) {
        const sprite = this.scene.add.sprite(cx, cy, `hero_${key}`)
          .setDisplaySize(80, 80).setOrigin(0.5).setDepth(depth + 1);
        // Match SaveSlotCard / MenuScene hero-portrait pipeline: LINEAR
        // texture filter + idle animation when registered.
        const tex = this.scene.textures.get(`hero_${key}`);
        const src = tex && tex.source && tex.source[0];
        if (src && typeof (src as { setFilter?: (m: number) => void }).setFilter === 'function') {
          (src as { setFilter: (m: number) => void }).setFilter(Phaser.Textures.FilterMode.LINEAR);
        }
        const idleKey = `hero_${key}_idle`;
        if (this.scene.anims?.exists?.(idleKey)) sprite.play(idleKey);
        created.push(sprite);
        return created;
      }
    }
    // Placeholder — neutral framed square with `?` glyph + "New player" caption.
    const frame = this.scene.add.rectangle(cx, cy - 4, 60, 60, 0x1a1208, 0.9)
      .setStrokeStyle(1, 0x5a3600, 0.6).setDepth(depth + 1);
    created.push(frame);
    const glyph = this.scene.add.text(cx, cy - 4, '?', {
      fontSize: '24px', fontFamily: 'Georgia, serif', color: '#5a4830', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(depth + 2);
    created.push(glyph);
    const caption = this.scene.add.text(cx, cy + 32, 'New player', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a4830', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(depth + 2);
    created.push(caption);
    return created;
  }

  // ---------- Input ----------

  private handleKey(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') { this.close(); ev.preventDefault(); return; }
    // Arrow-key pagination on every tab that lists entries (guild has none).
    if (this.activeTab !== 'guild') {
      if (ev.key === 'ArrowRight') { this.page++; this.refreshList(); ev.preventDefault(); return; }
      if (ev.key === 'ArrowLeft')  { this.page = Math.max(0, this.page - 1); this.refreshList(); ev.preventDefault(); return; }
    }
    if (!this.searchFocused) return;
    if (ev.key === 'Enter') { this.tryAddFromSearch(); ev.preventDefault(); return; }
    if (ev.key === 'Backspace') {
      if (this.searchDraft.length > 0) this.setSearch(this.searchDraft.slice(0, -1));
      ev.preventDefault();
      return;
    }
    if (ev.key.length === 1 && this.searchDraft.length < 32) {
      this.setSearch(this.searchDraft + ev.key);
      ev.preventDefault();
    }
  }

  // ---------- Lifecycle ----------

  close(): void {
    if (this._destroyed) return;
    this.opts.onClose?.();
    this.destroy();
  }

  /** True once destroy() has run — used by call sites like the BattleScene
   *  openSocial toggle to detect zombie references (scene-shutdown destroyed
   *  the hub but skipped onClose, leaving the parent scene's `_activeSocialHub`
   *  pointer dangling until F5). Replaces the previous "trust the pointer"
   *  check which made the button look broken until a page reload. */
  isDestroyed(): boolean {
    return this._destroyed;
  }

  destroy(): void {
    if (this._destroyed) return;
    this._destroyed = true;
    if (this.keyHandler) { window.removeEventListener('keydown', this.keyHandler, true); this.keyHandler = undefined; }
    this.unsubUpdate?.();
    this.clearRows();
    this.tabsContainer.destroy();
    this.root.destroy();
    this.dim?.destroy();
  }

  private _destroyed = false;
}
